import type { StoreDeliveryPrice } from "@khazzin/api-client";
import { formatMoney } from "@khazzin/i18n/money";
import { EmptyState, ErrorState, PageHeader } from "@khazzin/ui/patterns/page";
import { Input, SkeletonTable } from "@khazzin/ui/primitives";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";

/**
 * Delivery rates (EP10-S08, DA-10-10): what the store pays per destination,
 * province by province, a city's own price under its province. Read-only, and
 * the payload has only the client price — no carrier take, margin or level.
 */

export interface ProvinceGroup {
	key: string;
	companyName: string;
	provinceName: string;
	province: StoreDeliveryPrice | undefined;
	cities: StoreDeliveryPrice[];
}

/** Groups the flat list carrier → province, the province's own row first. */
export function groupByProvince(items: readonly StoreDeliveryPrice[]): ProvinceGroup[] {
	const groups = new Map<string, ProvinceGroup>();
	for (const item of items) {
		const key = `${item.companyId}/${item.provinceId}`;
		const group = groups.get(key) ?? {
			key,
			companyName: item.companyName,
			provinceName: item.provinceName || item.provinceCode,
			province: undefined,
			cities: [],
		};
		if (item.cityId) {
			group.cities.push(item);
		} else {
			group.province = item;
		}
		groups.set(key, group);
	}
	return [...groups.values()];
}

/**
 * The groups a search keeps (ADJ-M03-4): a province or a carrier that
 * matches keeps all its cities; otherwise only the cities that match, under
 * their province. Case- and accent-insensitive; empty keeps everything.
 */
export function filterGroups(groups: readonly ProvinceGroup[], q: string): ProvinceGroup[] {
	const fold = (s: string) =>
		s
			.normalize("NFD")
			.replace(/\p{Diacritic}/gu, "")
			.toLowerCase();
	const needle = fold(q.trim());
	if (needle === "") {
		return [...groups];
	}
	return groups.flatMap((group) => {
		if (fold(`${group.provinceName} ${group.companyName}`).includes(needle)) {
			return [group];
		}
		const cities = group.cities.filter((city) =>
			fold(city.cityName ?? city.cityId ?? "").includes(needle),
		);
		return cities.length === 0 ? [] : [{ ...group, cities }];
	});
}

export function DeliveryRatesPage() {
	const { t, locale } = useLocale();
	const { store } = useStoreScope();
	const api = useApi();
	const storeId = store?.storeId ?? "";
	const level = levelOf(store, "delivery_rates");
	// Filters the list on the page: it is the whole of the store's prices.
	const [q, setQ] = useState("");
	const rates = useQuery({
		queryKey: ["portal", storeId, "delivery-rates"],
		queryFn: ({ signal }) => api.listPortalDeliveryRates({ signal }),
		enabled: storeId !== "" && level !== "none",
	});

	if (store !== null && level === "none") {
		return <NotFound />;
	}
	const all = groupByProvince(rates.data?.items ?? []);
	const groups = filterGroups(all, q);
	const price = (item: StoreDeliveryPrice) => formatMoney(item.revenuePerOrder, locale);

	return (
		<div className="flex flex-col gap-4">
			<PageHeader
				title={t("portal.deliveryRates.title")}
				subtitle={t("portal.deliveryRates.subtitle")}
			/>
			{all.length > 0 && (
				<Input
					type="search"
					value={q}
					onChange={(event) => setQ(event.target.value)}
					aria-label={t("portal.deliveryRates.search")}
					placeholder={t("portal.deliveryRates.search")}
					className="max-w-72"
				/>
			)}
			{rates.isLoading ? (
				<SkeletonTable rows={6} />
			) : rates.isError ? (
				<ErrorState error={rates.error} onRetry={() => void rates.refetch()} t={t} />
			) : all.length === 0 ? (
				<EmptyState title={t("portal.deliveryRates.empty")} />
			) : groups.length === 0 ? (
				<EmptyState title={t("portal.deliveryRates.noMatch", { q })} />
			) : (
				<div className="overflow-x-auto card">
					<table className="w-full text-[13px]">
						<caption className="sr-only">{t("portal.deliveryRates.title")}</caption>
						<thead>
							<tr className="border-b border-border text-start text-fg-muted">
								<th scope="col" className="px-3 py-2 text-start">
									{t("portal.deliveryRates.columns.destination")}
								</th>
								<th scope="col" className="px-3 py-2 text-start">
									{t("portal.deliveryRates.columns.carrier")}
								</th>
								<th scope="col" className="px-3 py-2 text-end">
									{t("portal.deliveryRates.columns.price")}
								</th>
							</tr>
						</thead>
						<tbody>
							{groups.map((group) => [
								<tr key={group.key} className="border-b border-border bg-bg-subtle">
									<th scope="row" className="px-3 py-2 text-start font-bold">
										{group.provinceName}
									</th>
									<td className="px-3 py-2">{group.companyName}</td>
									<td className="px-3 py-2 text-end font-bold [font-variant-numeric:tabular-nums]">
										{group.province ? price(group.province) : "—"}
									</td>
								</tr>,
								...group.cities.map((city) => (
									<tr key={`${group.key}/${city.cityId}`} className="border-b border-border">
										<th scope="row" className="px-3 py-2 ps-8 text-start font-normal">
											{city.cityName ?? city.cityId}
										</th>
										<td className="px-3 py-2">{city.companyName}</td>
										<td className="px-3 py-2 text-end [font-variant-numeric:tabular-nums]">
											{price(city)}
											{group.province && (
												<span className="block text-[11.5px] text-fg-muted">
													{t("portal.deliveryRates.overrides", { price: price(group.province) })}
												</span>
											)}
										</td>
									</tr>
								)),
							])}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
